const BRAZIL_PROVINCE_BY_UF = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

class CartShippingCalculator extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.field__input');
    this.submitButton = this.querySelector('.cart-shipping-calculator__submit');
    this.message = this.querySelector('.cart-shipping-calculator__message');
    this.ratesList = this.querySelector('.cart-shipping-calculator__rates');
    this.country = this.dataset.country || 'Brazil';
    this.currency = this.dataset.currency || 'BRL';
    this.locale = this.dataset.locale || 'pt-BR';
    this.freeShippingThresholdCents = Number(this.dataset.freeShippingThreshold) || null;

    this.input.addEventListener('input', this.formatZip.bind(this));
    this.input.addEventListener('keydown', (event) => {
      if (event.code === 'Enter') {
        event.preventDefault();
        this.calculate();
      }
    });
    this.submitButton.addEventListener('click', this.calculate.bind(this));
  }

  formatZip(event) {
    let digits = event.target.value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      digits = `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    event.target.value = digits;
  }

  async calculate() {
    const zip = this.input.value.replace(/\D/g, '');

    if (zip.length !== 8) {
      this.showMessage('Digite um CEP válido.');
      this.hideRates();
      return;
    }

    this.submitButton.disabled = true;
    this.hideRates();
    this.showMessage('Buscando endereço...');

    try {
      const province = await this.lookupProvince(zip);

      if (!province) {
        this.showMessage('CEP não encontrado. Confira e tente novamente.');
        return;
      }

      this.showMessage('Calculando frete...');

      const params = [
        `shipping_address%5Bzip%5D=${encodeURIComponent(zip)}`,
        `shipping_address%5Bcountry%5D=${encodeURIComponent(this.country)}`,
        `shipping_address%5Bprovince%5D=${encodeURIComponent(province)}`,
      ].join('&');

      await fetch(`${routes.cart_url}/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      const rates = await this.pollRates(params);

      if (!rates || rates.length === 0) {
        this.showMessage('Nenhuma opção de frete disponível para este CEP.');
        return;
      }

      const isFreeShipping = await this.qualifiesForFreeShipping();
      this.renderRates(rates, isFreeShipping);
    } catch (error) {
      console.error('[cart-shipping-calculator] calculate error', error);
      this.showMessage('Não foi possível calcular o frete agora. Tente novamente.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  async lookupProvince(zip) {
    const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
    const data = await response.json();

    if (!response.ok || data.erro) {
      return null;
    }

    return BRAZIL_PROVINCE_BY_UF[data.uf] || null;
  }

  async qualifiesForFreeShipping() {
    if (!this.freeShippingThresholdCents) return false;

    const response = await fetch(`${routes.cart_url}.js`);
    const cart = await response.json();
    return cart.total_price >= this.freeShippingThresholdCents;
  }

  async pollRates(params, attempt = 0) {
    if (attempt >= 10) return null;

    const response = await fetch(`${routes.cart_url}/async_shipping_rates.json?${params}`);

    if (!response.ok) {
      throw new Error(`Shipping rates request failed with status ${response.status}`);
    }

    const bodyText = await response.text();
    const data = bodyText ? JSON.parse(bodyText) : null;

    if (data && data.shipping_rates) {
      return data.shipping_rates;
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));
    return this.pollRates(params, attempt + 1);
  }

  formatDeliveryDays(rate) {
    const [min, max] = rate.delivery_days || [];

    if (!min) return '';

    const pluralize = (days) => (days === 1 ? 'dia útil' : 'dias úteis');

    if (!max || max === min) {
      return `${min} ${pluralize(min)}`;
    }

    return `${min} a ${max} ${pluralize(max)}`;
  }

  renderRates(rates, isFreeShipping) {
    const formatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });

    this.ratesList.innerHTML = rates
      .map((rate) => {
        const deliveryText = this.formatDeliveryDays(rate);

        return `
          <li class="cart-shipping-calculator__rate">
            <span class="cart-shipping-calculator__rate-info">
              <span class="cart-shipping-calculator__rate-name">${rate.name}</span>
              ${deliveryText ? `<span class="cart-shipping-calculator__rate-delivery">${deliveryText}</span>` : ''}
            </span>
            <span>${isFreeShipping ? 'Grátis' : formatter.format(Number(rate.price))}</span>
          </li>
        `;
      })
      .join('');

    this.ratesList.hidden = false;
    this.showMessage('');
  }

  hideRates() {
    this.ratesList.hidden = true;
    this.ratesList.innerHTML = '';
  }

  showMessage(text) {
    this.message.textContent = text;
  }
}

customElements.define('cart-shipping-calculator', CartShippingCalculator);
